import * as H from 'history'
import { Fragment, useState, useEffect } from 'react'
import moment from 'moment'
import { useHistory, useLocation } from 'react-router-dom'

import { InfraAttack } from 'types/attacks/infra-attack'
import { Metadata } from 'api'
import duration from 'duration'
import {
  getTargetType,
  getTargetingStrategy,
  getCommand,
  isK8sAttack,
  getAttackConfiguration,
  isK8sTargetType
} from 'components/attacks/utils/attack-configuration'
import theme from 'theme'
import { track } from 'track'
import agent from 'agent'
import useAuth from 'hooks/use-auth'
import { getFormattedAttackType, getArgDisplayData } from 'utils/metadata'
import Button from 'components/common/Button'
import Stage from 'components/attacks/details/Stage'
import HaltConfirmation from 'components/attacks/HaltConfirmation'
import {
  PanelHeader,
  PanelTitle,
  PanelDetailsGrid,
  Label,
  Value,
  PanelSection
} from 'components/attacks/details/AttackStyles'
import { STAGES } from 'components/attacks/utils/attack-stages'
import Pill from 'components/common/Pill'

const RerunAttackButton: React.FC<{ rerunLinkTo: H.LocationDescriptor }> = ({
  rerunLinkTo
}) => {
  const history = useHistory()

  return (
    <Button
      data-test-rerun-attack
      data-track-action="rerun-attack"
      data-track-category="attack"
      data-track-on="click"
      onClick={() => {
        history.push(rerunLinkTo)
        track('RerunAttack')
      }}
    >
      Rerun
    </Button>
  )
}

const DetailPanel: React.FC<{
  attack: InfraAttack
  isScenario?: boolean
  metadata: Metadata
  doFetch(): void
  rerunLinkTo: H.LocationDescriptor
  showRerunButton?: boolean
}> = ({
  attack,
  isScenario,
  metadata,
  doFetch,
  rerunLinkTo,
  showRerunButton
}) => {
  const { currentTeamId } = useAuth()
  const history = useHistory()
  const { search: queryParams = '' } = useLocation()
  const attackConfiguration = getAttackConfiguration(attack)

  const [haltConfirmationOpen, setHaltConfirmationOpen] = useState(false)
  const { guid: attackId } = attack
  const command = getCommand(attackConfiguration)
  const targetingStrategy = getTargetingStrategy(attackConfiguration)
  const targetType = getTargetType(attackConfiguration)

  useEffect(() => {
    const params = new URLSearchParams(queryParams.replace('?', ''))
    const userAction = params.get('userAction')
    if (userAction) {
      if (userAction === 'rerun') {
        history.replace(rerunLinkTo)
      } else if (userAction === 'halt') {
        setTimeout(() => {
          setHaltConfirmationOpen(true)
        }, 200)
      }
    }
  }, [queryParams, attack, history, rerunLinkTo])

  const k8sAttackError = () => {
    if (isK8sAttack(attack) && attack.stage === 'ERROR') {
      if (attack.error) {
        return `: ${attack.error}`
      } else {
        return ': Could not resolve to any containers'
      }
    } else {
      return ''
    }
  }

  const showHalt =
    STAGES.IN_FLIGHT.some(stage => stage === attack.stage) ||
    (isK8sAttack(attack) && attack.stage === 'RESOLVING')

  const showContainerNames =
    isK8sTargetType(targetingStrategy) &&
    targetingStrategy.hasOwnProperty('containerSelection') &&
    (targetingStrategy.containerSelection.selectionType === 'ALL' ||
      (targetingStrategy.containerSelection.selectionType === 'EXACT' &&
        targetingStrategy.containerSelection.containerNames?.length > 0))

  const displayableArgs = getArgDisplayData(
    attackConfiguration.command,
    metadata
  )
  return (
    <main>
      <PanelSection css={{ display: 'flex' }}>
        <div css={{ flex: 1 }}>
          <PanelHeader>
            <PanelTitle>Configuration</PanelTitle>
          </PanelHeader>
          <PanelDetailsGrid>
            <Label>Type</Label>
            <Value>{getFormattedAttackType(command.infraCommandType)}</Value>
            <Label>Target Type</Label>
            <Value>{targetType}</Value>
            {displayableArgs
              .filter(
                ({ infraCommandArgKey }) => infraCommandArgKey !== 'length'
              )
              .map(({ displayName, value, short }) => (
                <Fragment key={short}>
                  <Label>{displayName}</Label>
                  <Value>{value.toString()}</Value>
                </Fragment>
              ))}
            {command.infraCommandArgs.length && (
              <Fragment>
                <Label>Length</Label>
                <Value>{duration(command.infraCommandArgs.length)}</Value>
              </Fragment>
            )}
            {command.infraCommandArgs.providers && (
              <Fragment>
                <Label css={{ lineHeight: '30px' }}>Providers</Label>
                <Value>
                  {command.infraCommandArgs.providers.map(value => {
                    return (
                      <Pill
                        key={`${attack.guid}-provider-${value}`}
                        showPointer
                        tooltip={value}
                      >
                        {value}
                      </Pill>
                    )
                  })}
                </Value>
              </Fragment>
            )}
            {isK8sTargetType(targetingStrategy) && showContainerNames && (
              <Fragment>
                <Label
                  css={{
                    lineHeight:
                      targetingStrategy.containerSelection.selectionType ===
                      'ALL'
                        ? 'initial'
                        : '30px'
                  }}
                >
                  Targeted Containers
                </Label>
                <Value>
                  {targetingStrategy.containerSelection.selectionType ===
                  'ALL' ? (
                    <Value>All</Value>
                  ) : (
                    targetingStrategy.containerSelection.containerNames.map(
                      value => {
                        return (
                          <Pill
                            key={`${attack.guid}-container-name-${value}`}
                            tooltip={value}
                          >
                            {value}
                          </Pill>
                        )
                      }
                    )
                  )}
                </Value>
              </Fragment>
            )}
          </PanelDetailsGrid>
        </div>
        <div css={{ flex: 1, marginLeft: theme.space[5] }}>
          <PanelHeader>
            <HaltConfirmation
              fetchAttack={doFetch}
              guid={attackId}
              open={haltConfirmationOpen}
              title="Halt attack"
              trackedEvent="SingleAttackHalted"
              trackingOptions={{ AttackId: attackId }} //scenarioId: id, runNumber: runNumber
              type="attack"
              onCancel={() => setHaltConfirmationOpen(false)}
              onConfirmRequest={({ reason }) => {
                if (isK8sAttack(attack) && attack.stage === 'RESOLVING') {
                  return agent.post(
                    `/kubernetes/attacks/${attack.uid}/halt?teamId=${currentTeamId}`
                  )
                }

                return agent.post(
                  `/attacks/${attackId}/halt?teamId=${currentTeamId}`,
                  { reason }
                )
              }}
            />

            <PanelTitle>Details</PanelTitle>
            {!isScenario && (
              <div css={{ marginLeft: 'auto' }}>
                {showHalt ? (
                  <Button
                    data-test-halt-attack
                    destructive
                    onClick={() => setHaltConfirmationOpen(true)}
                  >
                    Halt
                  </Button>
                ) : (
                  showRerunButton &&
                  rerunLinkTo && <RerunAttackButton rerunLinkTo={rerunLinkTo} />
                )}
              </div>
            )}
          </PanelHeader>
          <PanelDetailsGrid>
            <Label>Stage</Label>
            <Stage error={k8sAttackError()} stage={attack.stage} />
            <Label>User</Label>
            <Value>{attack.create_user}</Value>
            <Label>Kind</Label>
            <Value>{attack.kind}</Value>
            <Label>Started</Label>
            <Value>
              {moment(new Date(attack.start_time)).format('M/D/YYYY h:mm a')}
            </Value>
            {attack.end_time && (
              <Fragment>
                <Label>Ended</Label>
                <Value>
                  {moment(new Date(attack.end_time)).format('M/D/YYYY h:mm a')}
                </Value>
              </Fragment>
            )}
          </PanelDetailsGrid>
        </div>
      </PanelSection>
    </main>
  )
}

export default DetailPanel
